import { useSelector } from 'react-redux';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { AddIcon, LockedIcon } from 'foundations-assets';
import { useHasScope } from 'automation-ui-core/hooks/authHooks';
import EmptyState from 'automation-ui-canvas/components/embed/EmptyState';
import * as lockTypes from 'automation-ui-canvas/constants/EmbeddedFlowLockTypes';
import EmbeddedUpgradeButton from 'automation-ui-canvas/components/canvas/EmbeddedUpgradeButton';
import { getSourceApp, getEmptyStateName } from 'automation-ui-canvas/selectors/embedSelectors';
import * as Scopes from 'automation-ui-canvas/constants/Scopes';
import { getPqlConfigForFlowObjectTypeId } from 'automation-ui-action-features/access/PqlModalConfigs';

// local utils:
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getLockType = (flowObjectTypeIsLocked, hasWorkflowsAccess, hasCreateWorkflowAccess) => {
  if (!hasCreateWorkflowAccess) {
    return lockTypes.WORKFLOW_READ_ONLY_LOCK;
  }
  if (!hasWorkflowsAccess) {
    return lockTypes.WORKFLOW_ACCESS_DENIED_LOCK;
  }
  if (flowObjectTypeIsLocked) {
    return lockTypes.FLOW_OBJECT_TYPE_LOCK;
  } // Dev note: Flows created in the embed do not count against
  //   the portal's workflow limit. If this policy changes, add
  //   a check here to set WORKFLOW_LIMITS_ENTERPRISE_LOCK or
  //   WORKFLOW_LIMITS_LOCK
  return null;
};
// TODO - break this component up into multiple pieces to clarify locked upgrade vs. create new workflow functionality
const EmbeddedCreateFlowButton = ({
  onClick,
  arrPosition,
  doesWorkflowExist = false,
  flowObjectTypeId,
  flowObjectTypeIsLocked,
  upgradeModalKey
}) => {
  const emptyStateName = useSelector(getEmptyStateName);
  const sourceAppId = useSelector(getSourceApp);
  const hasCreateWorkflowAccess = useHasScope(Scopes.WORKFLOWS_PERMISSION_CREATE_WORKFLOW_TEAM_OWNED);
  const hasWorkflowsAccess = useHasScope(Scopes.WORKFLOWS_ACCESS);
  const lockType = getLockType(flowObjectTypeIsLocked, hasWorkflowsAccess, hasCreateWorkflowAccess);
  const isDisabledLockType = lockTypes.DISABLED_LOCK_TYPES.includes(lockType);
  let config;
  if (lockType && !isDisabledLockType) {
    config = getPqlConfigForFlowObjectTypeId(flowObjectTypeId);
  }
  const LOCK_TYPE_MESSAGES = {
    // Dev note, use message createWorkflow.workflowLimitTooltip for WORKFLOW_LIMITS_ENTERPRISE_LOCK
    [lockTypes.WORKFLOW_READ_ONLY_LOCK]: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "list.accessDisabled.newWorkflowTooltip"
    })
  };
  const lockDisabledMessage = LOCK_TYPE_MESSAGES[lockType];
  if (emptyStateName && arrPosition !== null && arrPosition !== undefined) {
    return /*#__PURE__*/_jsx("div", {
      "data-test-id": "embedded-new-flow-empty-state",
      className: "p-top-5",
      children: /*#__PURE__*/_jsx(EmptyState, {
        arrPosition: arrPosition,
        config: config,
        disabled: isDisabledLockType,
        disabledMessage: lockDisabledMessage,
        lockType: lockType,
        onNewFlowButtonClicked: onClick,
        use: emptyStateName
      })
    });
  }
  const ButtonComponent = config ? EmbeddedUpgradeButton : Button;
  const extraProps = config ? {
    uniqueId: `create-flow-cta-${arrPosition}`,
    config,
    screen: `embedded-workflow-create-${sourceAppId}`
  } : {};
  const handleOnClick = () => {
    if (lockType) {
      return;
    }
    onClick();
  };
  return /*#__PURE__*/_jsx(View, {
    marginBlockStart: "500",
    children: /*#__PURE__*/_jsx(UITooltip, {
      title: lockDisabledMessage,
      disabled: !isDisabledLockType,
      placement: "bottom right",
      children: /*#__PURE__*/_jsxs(ButtonComponent, Object.assign({
        use: "unstyled",
        onClick: handleOnClick,
        "data-test-id": "embedded-new-flow-button",
        disabled: isDisabledLockType,
        upgradeModalKey: upgradeModalKey
      }, extraProps, {
        children: [/*#__PURE__*/_jsx(IconCircle, {
          icon: AddIcon,
          color: "icon-primary-on-fill-default",
          backgroundColor: "fill-tertiary-pressed",
          size: "200",
          borderWidth: "0",
          padding: "50"
        }), doesWorkflowExist ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation.embed.flowCanvasContainer.createWorkflowActionButton"
        }) : /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "automation.embed.createWorkflow"
        }), lockType && /*#__PURE__*/_jsx(LockedIcon, {})]
      }))
    })
  });
};
export default EmbeddedCreateFlowButton;